/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface MedicalFacilityUnitProps {
  id: string;
  name: string;
  phoneNumber: string | null;
  medicalFacilityId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class MedicalFacilityUnitRecord extends Record<MedicalFacilityUnitProps>({
  id: null as any,
  name: null as any,
  phoneNumber: null as any,
  medicalFacilityId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    phoneNumber: string | null;
    medicalFacilityId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): MedicalFacilityUnitRecord {
    if (json == null) {
      throw new Error('MedicalFacilityUnitRecord.fromJS: parameter cannot be null');
    }
    return new MedicalFacilityUnitRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'MedicalFacilityUnitJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'MedicalFacilityUnitJson.name'),
      phoneNumber: json.phoneNumber,
      medicalFacilityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
        'MedicalFacilityUnitJson.medicalFacilityId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    phoneNumber: string | null;
    medicalFacilityId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [MedicalFacilityUnitRecord, Error[]] {
    if (json == null) {
      throw new Error('MedicalFacilityUnitRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new MedicalFacilityUnitRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'MedicalFacilityUnitJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'MedicalFacilityUnitJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      phoneNumber: tryOrDefault(
        () => json.phoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
            'MedicalFacilityUnitJson.medicalFacilityId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: MedicalFacilityUnitProps) {
    super(props);
    if (props == null) {
      throw new Error('MedicalFacilityUnitRecord constructor parameter cannot be null');
    }
  }
}

export abstract class MedicalFacilityUnitViewModelBase implements MedicalFacilityUnitProps {
  protected readonly _props: MedicalFacilityUnitProps;
  private _json?: MedicalFacilityUnitProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get phoneNumber(): string | null {
    return this._props.phoneNumber;
  }
  get medicalFacilityId(): string {
    return this._props.medicalFacilityId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: MedicalFacilityUnitProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        phoneNumber: this._props.phoneNumber,
        medicalFacilityId: this._props.medicalFacilityId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllMedicalFacilityUnitEntities = (state: AppState) =>
  state.entityStores.medicalFacilityUnit.entities;

export const selectMedicalFacilityUnitById = (id: string) => (state: AppState) =>
  state.entityStores.medicalFacilityUnit.entities.get(id);

export const selectMedicalFacilityUnitMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.medicalFacilityUnit.meta.get(id);

export const selectMedicalFacilityUnitAndMetadataById = (id: string) =>
  createSelector(
    selectMedicalFacilityUnitById(id),
    selectMedicalFacilityUnitMetadataById(id),
    (medicalFacilityUnit, meta) => ({ medicalFacilityUnit, meta }),
  );

export function selectMedicalFacilityUnitEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllMedicalFacilityUnitEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, MedicalFacilityUnitRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectMedicalFacilityUnitEntitiesByName(name: string) {
  return createSelector(selectAllMedicalFacilityUnitEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectMedicalFacilityUnitEntitiesByPhoneNumber(phoneNumber: string) {
  return createSelector(selectAllMedicalFacilityUnitEntities, (entities) =>
    entities.filter((e) => e.phoneNumber === phoneNumber).valueSeq(),
  );
}

export function selectMedicalFacilityUnitEntitiesByMedicalFacilityId(medicalFacilityId: string) {
  return createSelector(selectAllMedicalFacilityUnitEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityId === medicalFacilityId).valueSeq(),
  );
}
//#endregion Selectors
